import { Dispatch, SetStateAction, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../shared/pages-config';
import { ModuleList } from '../../shared/analytics/module-list';
import { LinkTitle } from './nav-links';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { useUserSettings } from '../../utils/use-user-settings';
import LockCustomIcon from '../../shared/enterprise-upsell-page/lock-custom-icon';
import { enterpriseUpsellPages } from '../../shared/enterprise-upsell-page/enterprise-pages-with-landing-page-enum';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import ListItem from '@mui/material/ListItem';
import { popoverClasses } from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
interface NavDropdownProps {
  drawerOpen: boolean;
  linkHref: string;
  linkTitle: string;
  icon: React.JSX.Element;
  subLinks?: {
    linkHref: string;
    linkTitle: string;
    disabledReason?: string | null;
  }[];
  disabledReason?: string | null;
  setCurrentNavListOpen: Dispatch<SetStateAction<LinkTitle>>;
  currentNavListOpen: LinkTitle;
}

export default function NavDropdown({
  linkTitle,
  subLinks,
  icon,
  drawerOpen,
  linkHref,
  disabledReason,
  setCurrentNavListOpen,
  currentNavListOpen,
}: NavDropdownProps) {
  const [currentlyHovering, setCurrentlyHovering] = useState(false);
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { features, currentTier } = useUserSettings();
  const enterpriseUpsellEnabled =
    isFeatureEnabled('enterpriseFeatureUpsellUiReact', features) &&
    currentTier !== 'enterprise';

  const isUpsellPageExists = (name: string) => {
    return enterpriseUpsellPages.some((page) => page.key === name);
  };

  const open = currentNavListOpen === linkTitle;

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (subLinks && subLinks.length === 0) {
    return null;
  }

  const isLight = theme.palette.mode === 'light';
  const colorMain = theme.palette.colors.primary[700];

  const navColors = {
    text: isLight ? theme.palette.text.secondary : theme.palette.text.primary,

    selectedText: isLight
      ? theme.palette.primary.main
      : theme.palette.text.primary,

    selectedBg: isLight
      ? theme.palette.primary.light
      : theme.palette.primary.main,

    selectedBgWithSubmenu: isLight
      ? theme.palette.primary.light
      : drawerOpen
      ? theme.palette.primary.dark
      : theme.palette.primary.main,

    hoverBg: isLight ? theme.palette.semantic.surface.bg3 : colorMain,

    indicator: isLight
      ? theme.palette.primary.main
      : theme.palette.text.primary,

    inactiveIndicator: theme.palette.grey[300],
  };

  const navButtonSx = {
    borderRadius: 1,
    color: navColors.text,

    '&.Mui-selected': {
      backgroundColor: navColors.selectedBg,
      color: navColors.selectedText,
    },

    '&:hover': {
      backgroundColor: navColors.hoverBg,
    },
  };

  const navButtonWithSubLinkSx = {
    borderRadius: 1,
    color: navColors.text,

    '&.Mui-selected': {
      backgroundColor: navColors.selectedBgWithSubmenu,
      color: navColors.selectedText,
    },

    '&:hover': {
      backgroundColor: navColors.hoverBg,
    },
  };

  const handleCloseHover = () => {
    setCurrentlyHovering(false);
    setTimeout(() => {
      if (!currentlyHovering) {
        handleClose();
      }
    }, 50);
  };

  const locationStartsWith = (link: string) =>
    location.pathname.split('/')[1] === link;

  const isNestedUserSection =
    locationStartsWith(pagesConfig.users.link) &&
    linkTitle === pagesConfig.users.name &&
    !location.pathname.includes(pagesConfig.roleManagerLimitedVisibility.link);

  const isNestedGroupSection =
    locationStartsWith(pagesConfig.groups.link) && linkTitle === 'Directory';

  const isNestedIndexSection =
    location.pathname.includes(pagesConfig.indexing.link) &&
    linkTitle === ModuleList.modules.administration.name;

  const isNestedSettingsSection =
    locationStartsWith(pagesConfig.settings.link) &&
    linkTitle === ModuleList.modules.administration.name &&
    !location.pathname.includes(pagesConfig.roleManager.link) &&
    !location.pathname.includes(pagesConfig.gmailSettings.link) &&
    !location.pathname.includes(pagesConfig.businessGroupSettings.link);

  const isNestedDecomSection =
    locationStartsWith(pagesConfig.decommissioningLayout.link) &&
    linkTitle === ModuleList.modules.automation.name;

  const isNestedResourceCalendarSection =
    locationStartsWith(pagesConfig.resourceCalendarsLayout.link) &&
    linkTitle === ModuleList.modules.calendars.name;

  const isNestedBillingSection =
    locationStartsWith(pagesConfig.billing.link) &&
    linkTitle === ModuleList.modules.billing.name;

  const isNestedRoleSection =
    locationStartsWith(pagesConfig.roleManager.link) &&
    linkTitle === ModuleList.modules.administration.name;
  const isNestedgPanelContactsSection = locationStartsWith(
    pagesConfig.gpanelContacts.link
  );
  return (
    <>
      {subLinks ? (
        <div onMouseLeave={handleCloseHover}>
          <ListItemButton
            aria-owns={anchorEl ? 'basic-menu' : undefined}
            aria-haspopup="true"
            key={subLinks && subLinks[0].linkTitle}
            onClick={() => {
              if (drawerOpen) {
                setCurrentNavListOpen(open ? '' : (linkTitle as LinkTitle));
              }
            }}
            onMouseOver={(event) =>
              !drawerOpen ? setAnchorEl(event.currentTarget) : undefined
            }
            sx={{
              paddingLeft: '8px',
              paddingRight: '8px',
              ...navButtonWithSubLinkSx,
              mb: drawerOpen ? 1 : 0,
            }}
            selected={
              (subLinks &&
                subLinks
                  .map((subLink) => subLink.linkHref)
                  .some(
                    (subLinkHref) =>
                      location.pathname.substring(1) === subLinkHref ||
                      locationStartsWith(subLinkHref)
                  )) ||
              isNestedGroupSection ||
              isNestedResourceCalendarSection ||
              isNestedDecomSection ||
              isNestedIndexSection ||
              isNestedSettingsSection ||
              isNestedRoleSection
            }
          >
            {drawerOpen ? (
              <>
                <ListItemIcon
                  sx={{
                    alignItems: 'center',
                    minWidth: 30,
                    ml: 2,
                    color: 'inherit',
                  }}
                >
                  {icon}
                </ListItemIcon>

                <ListItemText primary={linkTitle} sx={{ pl: 1 }} />
                {subLinks ? open ? <ExpandLess /> : <ExpandMore /> : null}
              </>
            ) : (
              <>
                <ListItemIcon
                  sx={{
                    textAlign: 'center',
                    flexDirection: 'row',
                    fontSize: '8px',
                    alignItems: 'center',
                    width: '56px',
                    textWrap: 'wrap',
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {icon}
                    {linkTitle === 'Administration' ? 'Admin' : linkTitle}
                  </div>

                  <div>
                    {subLinks ? <ChevronRight fontSize="small" /> : null}
                  </div>
                </ListItemIcon>
              </>
            )}
          </ListItemButton>
          {subLinks && (
            <Collapse
              in={!drawerOpen ? false : open}
              timeout="auto"
              unmountOnExit
            >
              <List disablePadding>
                {subLinks.map((subLink) => {
                  const disabledReason =
                    linkTitle === 'Bookmarks' ? null : subLink.disabledReason;
                  const isGroups = subLink.linkTitle === 'Groups';
                  const isResourceCalendars =
                    subLink.linkTitle ===
                    pagesConfig.resourceCalendarsLayout.name;
                  const isDecommissioning =
                    subLink.linkTitle ===
                    pagesConfig.decommissioningLayout.name;
                  const isIndexing =
                    subLink.linkTitle === pagesConfig.indexing.name;
                  const isSettings =
                    subLink.linkTitle === pagesConfig.settings.name;
                  const isBilling =
                    subLink.linkTitle === pagesConfig.billing.name;
                  const isRoleDetails =
                    subLink.linkTitle === pagesConfig.roleManager.name;
                  const isGpanelContacts =
                    subLink.linkTitle === pagesConfig.gpanelContacts.name;

                  return (
                    <Tooltip
                      key={subLink.linkTitle}
                      arrow
                      placement="right"
                      title={disabledReason}
                    >
                      <span>
                        <Stack
                          flexDirection="row"
                          justifyContent="center"
                          alignItems="center"
                          key={subLink.linkTitle}
                        >
                          <ListItemButton
                            dense
                            component={Link}
                            to={subLink.linkHref}
                            key={subLink.linkTitle}
                            disabled={disabledReason !== null}
                            selected={
                              location.pathname.substring(1) ===
                                subLink.linkHref ||
                              (isGroups && isNestedGroupSection) ||
                              (isResourceCalendars &&
                                isNestedResourceCalendarSection) ||
                              (isDecommissioning && isNestedDecomSection) ||
                              (isIndexing && isNestedIndexSection) ||
                              (isSettings && isNestedSettingsSection) ||
                              (isBilling && isNestedBillingSection) ||
                              (isRoleDetails && isNestedRoleSection) ||
                              (isGpanelContacts &&
                                isNestedgPanelContactsSection)
                            }
                            sx={{
                              ...navButtonSx,
                              pl: drawerOpen ? 4 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                                justifyContent: 'center',
                                bgcolor: () =>
                                  location.pathname.substring(1) ===
                                    subLink.linkHref ||
                                  locationStartsWith(subLink.linkHref) ||
                                  (isGroups && isNestedGroupSection) ||
                                  (isResourceCalendars &&
                                    isNestedResourceCalendarSection) ||
                                  (isDecommissioning && isNestedDecomSection) ||
                                  (isIndexing && isNestedIndexSection) ||
                                  (isSettings && isNestedSettingsSection) ||
                                  (isBilling && isNestedBillingSection) ||
                                  (isRoleDetails && isNestedRoleSection) ||
                                  (isGpanelContacts &&
                                    isNestedgPanelContactsSection)
                                    ? navColors.indicator
                                    : 'grey.300',
                                borderRadius: 2,
                                width: 3,
                                height: 20,
                                mr: drawerOpen ? 2 : 0,
                              }}
                            />
                            <ListItemText primary={subLink.linkTitle} />
                            {enterpriseUpsellEnabled &&
                            isUpsellPageExists(subLink.linkTitle) ? (
                              <LockCustomIcon />
                            ) : null}
                          </ListItemButton>
                        </Stack>
                      </span>
                    </Tooltip>
                  );
                })}
              </List>
            </Collapse>
          )}

          {/* Popout Menu */}
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            MenuListProps={{
              onMouseLeave: handleCloseHover,
              style: {
                pointerEvents: 'auto',
                paddingLeft: 4,
                paddingRight: 4,
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? theme.palette.background.paper
                    : theme.palette.drawerBlue.main,
              },
            }}
            sx={{
              [`&.${popoverClasses.root}`]: {
                pointerEvents: 'none',
              },
            }}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {subLinks.map((subLink) => {
              const disabledReason =
                linkTitle === 'Bookmarks' ? null : subLink.disabledReason;
              const isGroups = subLink.linkTitle === 'Groups';
              const isResourceCalendars =
                subLink.linkTitle === pagesConfig.resourceCalendarsLayout.name;
              const isDecommissioning =
                subLink.linkTitle === pagesConfig.decommissioningLayout.name;
              const isIndexing =
                subLink.linkTitle === pagesConfig.indexing.name;
              const isSettings =
                subLink.linkTitle === pagesConfig.settings.name;
              const isBilling = subLink.linkTitle === pagesConfig.billing.name;
              const isRoleDetails =
                subLink.linkTitle === pagesConfig.roleManager.name;
              const isGpanelContacts =
                subLink.linkTitle === pagesConfig.gpanelContacts.name;
              return (
                <Tooltip
                  key={subLink.linkTitle}
                  arrow
                  placement="right"
                  title={disabledReason}
                >
                  <span>
                    <ListItemButton
                      component={Link}
                      dense
                      to={subLink.linkHref}
                      key={subLink.linkTitle}
                      onClick={handleClose}
                      sx={{
                        textDecoration: 'none',
                        ...navButtonSx,
                      }}
                      selected={
                        location.pathname.substring(1) === subLink.linkHref ||
                        (isGroups && isNestedGroupSection) ||
                        (isResourceCalendars &&
                          isNestedResourceCalendarSection) ||
                        (isDecommissioning && isNestedDecomSection) ||
                        (isIndexing && isNestedIndexSection) ||
                        (isSettings && isNestedSettingsSection) ||
                        (isBilling && isNestedBillingSection) ||
                        (isRoleDetails && isNestedRoleSection) ||
                        (isGpanelContacts && isNestedgPanelContactsSection)
                      }
                      disabled={disabledReason !== null}
                    >
                      <ListItemText primary={subLink.linkTitle} />
                      {enterpriseUpsellEnabled &&
                      isUpsellPageExists(subLink.linkTitle) ? (
                        <LockCustomIcon />
                      ) : null}
                    </ListItemButton>
                  </span>
                </Tooltip>
              );
            })}
          </Menu>
        </div>
      ) : (
        <>
          {/* Links without dropdowns */}
          <Tooltip
            arrow
            placement="right"
            title={
              disabledReason && disabledReason.length > 0 ? disabledReason : ''
            }
          >
            <span>
              <ListItem
                key={linkTitle}
                disablePadding
                component={Link}
                to={disabledReason && disabledReason.length > 0 ? '' : linkHref}
                sx={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                }}
              >
                {drawerOpen ? (
                  <ListItemButton
                    sx={{
                      px: 1,
                      ...navButtonSx,
                    }}
                    selected={
                      location.pathname.substring(1) === linkHref ||
                      isNestedUserSection ||
                      isNestedBillingSection
                    }
                    disabled={
                      disabledReason && disabledReason.length > 0 ? true : false
                    }
                    onClick={() => {
                      navigate(linkHref);
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        alignItems: 'center',
                        minWidth: 30,
                        ml: 2,
                        color: 'inherit',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText primary={linkTitle} sx={{ pl: 1 }} />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    sx={{
                      px: 0,
                      ...navButtonSx,
                    }}
                    selected={
                      location.pathname.substring(1) === linkHref ||
                      isNestedUserSection ||
                      isNestedBillingSection
                    }
                    disabled={
                      disabledReason && disabledReason.length > 0 ? true : false
                    }
                    onClick={() => {
                      navigate(linkHref);
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        textAlign: 'center',
                        flexDirection: 'column',
                        fontSize: '8px',
                        alignItems: 'center',
                        width: '56px',
                        textWrap: 'wrap',
                        minWidth: '56px',
                        justifyContent: 'center',
                        color: 'inherit',
                      }}
                    >
                      {icon}{' '}
                      {linkTitle === 'User Management'
                        ? 'User Mgmt'
                        : linkTitle}
                    </ListItemIcon>
                  </ListItemButton>
                )}
              </ListItem>
            </span>
          </Tooltip>
        </>
      )}
    </>
  );
}
